"use client";

import { useCallback, useEffect, useState } from "react";

export type UnlockLead = { name: string; mobile: string; email?: string };

type StoredUnlock = UnlockLead & { expiresAt: number };

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;
const UNLOCK_EVENT = "aip-unlock-change";

export const unlockKey = (propertyId: string) => `aip_unlock:${propertyId}`;

export function isUnlocked(propertyId: string): boolean {
  if (typeof window === "undefined" || !propertyId) return false;
  try {
    const raw = window.localStorage.getItem(unlockKey(propertyId));
    if (!raw) return false;
    const stored = JSON.parse(raw) as StoredUnlock;
    if (typeof stored.expiresAt !== "number" || stored.expiresAt <= Date.now()) {
      window.localStorage.removeItem(unlockKey(propertyId));
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function setUnlocked(propertyId: string, lead: UnlockLead): void {
  if (typeof window === "undefined" || !propertyId) return;
  try {
    const stored: StoredUnlock = {
      ...lead,
      expiresAt: Date.now() + THIRTY_DAYS_MS,
    };
    window.localStorage.setItem(unlockKey(propertyId), JSON.stringify(stored));
    window.dispatchEvent(new CustomEvent(UNLOCK_EVENT, { detail: propertyId }));
  } catch {
    // Storage can be unavailable in private browsing or restricted contexts.
  }
}

export function useUnlock(propertyId: string): {
  unlocked: boolean;
  unlock: (lead: UnlockLead) => void;
} {
  const [unlocked, setUnlockedState] = useState(false);

  useEffect(() => {
    const refresh = () => setUnlockedState(isUnlocked(propertyId));
    refresh();
    window.addEventListener("storage", refresh);
    window.addEventListener(UNLOCK_EVENT, refresh);
    return () => {
      window.removeEventListener("storage", refresh);
      window.removeEventListener(UNLOCK_EVENT, refresh);
    };
  }, [propertyId]);

  const unlock = useCallback(
    (lead: UnlockLead) => {
      setUnlocked(propertyId, lead);
      setUnlockedState(true);
    },
    [propertyId]
  );

  return { unlocked, unlock };
}
